import Layout from '../../components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import debounce from 'lodash.debounce';
import { Card, Icon, IconType, VideoPlayer } from 'lucid-components-react';
import * as queryString from 'query-string';
import React, { MouseEvent, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

import AdditionalResources from '../../components/additional-resources/additional-resources';
import Footer from '../../components/footer/footer';
import Link from '../../components/link/link';
import Navbar from '../../components/navbar/navbar';
import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_FieldNodeCaseStudyHubFieldAdditionalResources,
  Drupal_FieldNodeCaseStudyHubFieldAdditionalResourcesCtas,
  Drupal_FieldNodeCaseStudyHubFieldHeroCta,
  Drupal_Metatag,
  Drupal_NodeCaseStudy,
  Drupal_NodeCaseStudyHub,
  Drupal_ParagraphAdditionalResources,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_TaxonomyTerm
} from '../../generated/graphql';
import {
  useMenuDefaultFooter,
  useMenuMainNavLinks
} from '../../hooks/use-menu';
import { TemplateProps } from '../../types/types';
import './case-study-hub.scss';
import { getEntityFields } from '../../utils/getEntityFields';
import useMarketoLink from '../../hooks/use-marketo-link';

interface Filter {
  value: string;
  label: string;
}

const CaseStudyHub = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeCaseStudyHub;
  const allTiles = drupal.nodeQuery!.entities as Drupal_NodeCaseStudy[];
  const filters = drupal.taxonomyTermQuery!.entities as Drupal_TaxonomyTerm[];
  const { i18nData } = data;

  const {
    i18nKey,
    page = 1,
    pageSize = 9,
    hubAlias = 'case-studies',
    hideSsr
  } = pageContext;
  const i18n = i18nFunct(i18nData, i18nKey);

  const filterGroups: Record<string, Array<Filter>> = {
    industry: [{ value: '', label: i18n('mktg_ct_cs_placeholder_1') }],
    size: [{ value: '', label: i18n('mktg_ct_cs_placeholder_2') }],
    role: [{ value: '', label: i18n('mktg_ct_cs_placeholder_3') }]
  };

  filters.forEach(filter => {
    const key = filter.entityBundle as keyof typeof filterGroups;
    const name = filter.name;
    const label = filter.entityTranslation
      ? filter.entityTranslation.entityLabel
      : name;
    if (name && label) {
      filterGroups[key].push({ value: name, label });
    }
  });

  const {
    industry: queryIndustry,
    size: querySize,
    role: queryRole,
    search: querySearch,
    page: queryPage
  } = queryString.parse(location.search) as Record<string, string>;

  const pagedAllTiles = allTiles.slice((page - 1) * pageSize);
  const [tiles, setTiles] = useState(pagedAllTiles);
  const [search, setSearch] = useState(querySearch);
  const [industry, setIndustry] = useState(queryIndustry);
  const [size, setSize] = useState(querySize);
  const [role, setRole] = useState(queryRole);

  const viewPage = queryPage ? parseInt(queryPage, 10) : page;
  const [currentPage, setCurrentPage] = useState(viewPage || 1);

  const handleSearch = debounce((value: string) => {
    setSearch(value);
  }, 150);

  const viewMore = (e: MouseEvent) => {
    e.preventDefault();
    const newCurrentPage = currentPage + 1;
    setCurrentPage(newCurrentPage);
  };

  const totalPageCount = Math.ceil(tiles.length / pageSize);
  const hasMoreWebinars = currentPage < totalPageCount;

  const filterTiles = () => {
    const searchFilters: Record<string, string> = {
      search,
      industry,
      size,
      role
    };

    const newQueryParams: Record<string, string | number> = {};

    const filterValues = Object.entries(searchFilters)
      .filter(entry => entry[1])
      .map(entry => {
        newQueryParams[entry[0]] = entry[1];
        return entry[1].toLowerCase();
      });

    if (currentPage > 1) {
      newQueryParams.page = currentPage;
    }

    const queryParams = queryString.stringify(newQueryParams);
    window.history.replaceState(
      {},
      '',
      `${location.pathname}${queryParams ? '?' : ''}${queryParams}`
    );

    const filteredTiles = (currentPage <= 1 ? allTiles : pagedAllTiles).filter(
      tile => {
        if (filterValues.length < 1) {
          return true;
        }

        const tileValues = [
          ...tile.fieldCompanyName?.toLowerCase().split(' '),
          tile.fieldSize?.entity?.name?.toLowerCase(),
          ...tile.fieldRole
            ?.filter(role => role?.entity?.name)
            .map(role => {
              const entity = role?.entity as Drupal_TaxonomyTerm;
              return entity.name?.toLowerCase();
            }),
          ...tile.fieldIndustry
            ?.filter(industry => industry?.entity?.name)
            .map(industry => {
              const entity = industry?.entity as Drupal_TaxonomyTerm;
              return entity.name?.toLowerCase();
            }),
          ...tile.fieldSearchTerms
            ?.filter(term => term?.entity?.name)
            .map(term => term?.entity?.name?.toLowerCase())
        ];

        const matches = filterValues.filter(
          filterValue =>
            tileValues.filter(tileValue => tileValue?.includes(filterValue))
              .length
        );

        return filterValues.length === matches.length;
      }
    );

    setTiles(filteredTiles);
  };

  useEffect(() => {
    filterTiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, industry, size, role, currentPage]);

  const { nid, langPrefix, pageType } = pageContext;

  const phonePrefix = langPrefix !== 'en' ? '+1 ' : '';
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const titleMetatag = (entityMetatags as Drupal_Metatag[]).find(
    ({ key }) => key === 'title'
  );
  const title = titleMetatag ? (
    <title key={titleMetatag.key!}>
      {`Page ${currentPage} for ${titleMetatag.value}`}
    </title>
  ) : null;

  const navbarMenu = useMenuMainNavLinks(langPrefix);
  const footerMenu = useMenuDefaultFooter(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  return (
    <Layout
      nid={nid}
      pageType={pageType}
      location={location}
      langPrefix={langPrefix}
      metatags={entityMetatags}
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
    >
      {currentPage > 1 && (
        <Helmet>
          {title}
          <meta name="robots" content="follow, noindex" />
        </Helmet>
      )}
      <Navbar menu={navbarMenu} />
      <LayoutContext.Consumer>
        {({ video, language }) => (
          <>
            <main className="case-study-hub">
              <section className="case-study-hub__hero">
                <div className="case-study-hub__container">
                  <div className="case-study-hub__video-container">
                    <VideoPlayer
                      className="case-study-hub__video"
                      ratio="square"
                      onClick={() =>
                        video.getVideoModal(
                          'https://player.vimeo.com/video/98592765',
                          true,
                          {
                            vimeo: {
                              playerOptions: {
                                texttrack:
                                  language.shortLangPrefix === 'pt'
                                    ? language.i18nKey
                                    : language.shortLangPrefix
                              }
                            }
                          }
                        )
                      }
                      placeholder={() => (
                        <img
                          alt={i18n('mktg-ct-cs-video-text')}
                          src="https://d2slcw3kip6qmk.cloudfront.net/marketing/images/HUB-Pearson-Image.png"
                        />
                      )}
                    />
                  </div>
                  <div className="case-study-hub__blurb">
                    <h1 className="case-study-hub__blurb-title">
                      {i18n('mktg-ct-cs-page-header')}
                    </h1>
                    <h2 className="case-study-hub__blurb-subtitle">
                      {i18n('mktg-ct-cs-cta-header')}
                    </h2>

                    {(node.fieldHeroCta as Drupal_FieldNodeCaseStudyHubFieldHeroCta[]).map(
                      (cta, i) => {
                        const ctaEntity = cta.entity as Drupal_ParagraphCtaButton;
                        const ctaMarketoForm = ctaEntity.fieldMarketo!
                          .entity as Drupal_ParagraphMarketoForm;
                        const buttonText = ctaEntity.fieldButtonText;
                        const defaultText =
                          i === 0
                            ? i18n('mktg-ct-all-get-a-quote')
                            : i18n('mktg-ct-all-request-a-demo');
                        const { url, copy } = getMarketoLink(
                          ctaMarketoForm.fieldMarketoId
                        );
                        return ctaEntity.fieldButtonLink ? (
                          <Link
                            asButton
                            primary={!ctaEntity.fieldIsSecondary}
                            secondary={!!ctaEntity.fieldIsSecondary}
                            key={ctaEntity.fieldButtonLink}
                            to={ctaEntity.fieldButtonLink}
                            className="case-study-hub__blurb-btn"
                          >
                            {buttonText || defaultText}
                          </Link>
                        ) : (
                          <Link
                            to={url}
                            asButton
                            key={ctaMarketoForm.fieldMarketoId!}
                            primary={!ctaEntity.fieldIsSecondary}
                            secondary={!!ctaEntity.fieldIsSecondary}
                            className="case-study-hub__blurb-btn"
                          >
                            {buttonText || copy}
                          </Link>
                        );
                      }
                    )}
                    <div>
                      <a
                        href="tel:+18772109890"
                        className="case-study-hub__blurb-phone"
                      >
                        {i18n('mktg-ct-cs-phone-number', {
                          phone: `${phonePrefix} (877) 210-9890`
                        })}
                      </a>
                    </div>
                  </div>
                </div>
              </section>
              <section className="case-study-hub__filter-section">
                <div className="case-study-hub__filter-section-content">
                  <h2 className="case-study-hub__filters-title">
                    {i18n('mktg-ct-cs-studies-header')}
                  </h2>
                  <div className="case-study-hub__search-wrap">
                    <input
                      placeholder="Search case studies"
                      className="case-study-hub__search-input"
                      type="text"
                      name="search"
                      defaultValue={search}
                      onChange={e => handleSearch(e.target.value)}
                    />
                  </div>
                  {allTiles.length > 10 && (
                    <div className="case-study-hub__filters">
                      <Select
                        className="case-study-hub__filter"
                        classNamePrefix="csh-filter"
                        options={filterGroups.industry}
                        value={filterGroups.industry.find(
                          groupIndustry => groupIndustry.label === industry
                        )}
                        isSearchable={false}
                        placeholder={i18n('mktg-ct-cs-placeholder-1')}
                        onChange={(selectedOption: any) =>
                          setIndustry(selectedOption?.value)
                        }
                        dropdownIndicator={() => {
                          return <div>Hello</div>;
                        }}
                      />
                      <Select
                        className="case-study-hub__filter"
                        classNamePrefix="csh-filter"
                        options={filterGroups.size}
                        value={filterGroups.size.find(
                          groupSize => groupSize.label === size
                        )}
                        isSearchable={false}
                        placeholder={i18n('mktg-ct-cs-placeholder-2')}
                        onChange={(selectedOption: any) =>
                          setSize(selectedOption.value)
                        }
                      />
                      <Select
                        className="case-study-hub__filter"
                        classNamePrefix="csh-filter"
                        options={filterGroups.role}
                        value={filterGroups.role.find(
                          groupRole => groupRole.label === role
                        )}
                        isSearchable={false}
                        placeholder={i18n('mktg-ct-cs-placeholder-3')}
                        onChange={(selectedOption: any) =>
                          setRole(selectedOption.value)
                        }
                      />
                    </div>
                  )}
                </div>
              </section>
              <section className="case-study-hub__tiles">
                {tiles
                  .slice(0, (currentPage - page + 1) * pageSize)
                  .map(study => (
                    <Link
                      key={study.entityUrl!.translate!.path!}
                      className="case-study-hub__tile"
                      to={study.entityUrl!.translate!.path!}
                      underline="none"
                      css={{ width: '100%' }}
                    >
                      <Card className="case-study-hub__tile-card" isInteractive>
                        <Card.Content css={{ padding: '0', maxHeight: '100%' }}>
                          <img
                            alt={study.title!}
                            className="case-study-hub__tile-image"
                            src={`${study.fieldHubLogo || study.fieldLogo!}`}
                          />
                        </Card.Content>
                      </Card>
                    </Link>
                  ))}
                {tiles.length < 1 && (
                  <div className="case-study-hub__empty">
                    <p>{i18n('mktg-ct-cs-empty-results')}</p>
                  </div>
                )}
              </section>
              <section className="case-study-hub__view-more">
                <hr />
                {hasMoreWebinars && (
                  <Link
                    className="case-study-hub__view-more-btn"
                    to={`${hubAlias}/${currentPage + 1}`}
                    onClick={viewMore}
                  >
                    {i18n('mktg-ct-cs-view-more')}
                    <div className="case-study-hub__view-more-btn-icon">
                      <Icon type={IconType.CarotDown} />
                    </div>
                  </Link>
                )}
              </section>
            </main>
            <AdditionalResources
              resources={(node.fieldAdditionalResources as Drupal_FieldNodeCaseStudyHubFieldAdditionalResources[]).map(
                ({ entity }) => entity as Drupal_ParagraphAdditionalResources
              )}
              ctas={(node.fieldAdditionalResourcesCtas as Drupal_FieldNodeCaseStudyHubFieldAdditionalResourcesCtas[]).map(
                ({ entity }) => entity as Drupal_ParagraphCtaButton
              )}
            />
          </>
        )}
      </LayoutContext.Consumer>
      <Footer menu={footerMenu} entityTranslations={entityTranslations} />
    </Layout>
  );
};

export default CaseStudyHub;

export const query = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $langPrefix: [String!]
  ) {
    drupal {
      nodeQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "type", value: ["case_study"], operator: IN }
            { field: "field_is_private", value: "0", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
            { field: "langcode", value: $langPrefix, operator: EQUAL }
          ]
        }
        sort: [
          { field: "field_weight", direction: ASC }
          { field: "field_company_name", direction: ASC }
        ]
      ) {
        entities {
          ... on Drupal_NodeCaseStudy {
            entityUrl {
              translate(language: $LanguageId) {
                path
              }
            }
            fieldCompanyName
            fieldLogo
            fieldHubLogo
            fieldRole {
              entity {
                name
              }
            }
            fieldSize {
              entity {
                name
              }
            }
            fieldIndustry {
              entity {
                name
              }
            }
            fieldSearchTerms {
              entity {
                name
              }
            }
          }
        }
      }
      taxonomyTermQuery(
        limit: 1000
        filter: {
          conditions: [
            { field: "vid", operator: IN, value: ["role", "industry", "size"] }
          ]
        }
      ) {
        entities {
          ... on Drupal_TaxonomyTerm {
            entityBundle
            name
            entityTranslation(language: $LanguageId) {
              entityLabel
            }
          }
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeCaseStudyHub {
          ...entityFields
          fieldHeroCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldAdditionalResources {
            entity {
              ... on Drupal_ParagraphAdditionalResources {
                fieldResourceUrl
                fieldResourceText
              }
            }
          }
          fieldAdditionalResourcesCtas {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_video_text
            mktg_ct_cs_page_header
            mktg_ct_cs_cta_header
            mktg_ct_cs_cta_primary
            mktg_ct_cs_cta_secondary
            mktg_ct_all_get_a_quote
            mktg_ct_all_request_a_demo
            mktg_ct_cs_phone_number
            mktg_ct_cs_studies_header
            mktg_ct_all_or_call
            mktg_ct_cs_placeholder_1
            mktg_ct_cs_placeholder_2
            mktg_ct_cs_placeholder_3
            mktg_ct_cs_empty_results
            mktg_ct_cs_view_more
          }
        }
      }
    }
  }
`;
